import { ProductNonStandardVendors } from "@iewc/Types/CustomModels";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ListItem, ListItemProps, UnorderedList, UnorderedListProps } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    returnPolicyContent = "returnPolicyContent",
}

const mapStateToProps = (state: ApplicationState) => ({
    products: getProductsForProductInfoList(state, "cmiProducts"),
});

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;

    /* Verndale Custom Feature - Add new styles for new accordion sections */
    servicesList?: UnorderedListProps;
    servicesListItem?: ListItemProps;
    cmiSubheading?: TypographyPresentationProps;
    cmiList?: UnorderedListProps;
    cmiListItem?: ListItemProps;
    additionalVendorsText?: TypographyPresentationProps;
    additionalVendorsLabel?: TypographyPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },

    /* Verndale Custom Feature - Set CMI accordion section subheading */
    cmiSubheading: {
        forwardAs: "h4",
        weight: 600,
    },
    additionalVendorsText: {
        forwardAs: "div",
    },
    additionalVendorsLabel: {
        weight: 700,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition, returnPolicyContent },
    products,
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && attributeTypes && attributeTypes?.length > 0;
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    /* Verndale Custom Feature - Remove condition to hide all widget content */

    /* Verndale Custom Feature - adding services accordion */
    const cmiProductsCount = +(properties["cmiProductsCount"] ?? 0);
    const isCmiProduct = properties["isCmiProduct"].toLowerCase() === "true";
    const hasValidProperties = ["packagingCategory", "serviceDesc", "spoolSize"].some(
        key => key in properties && properties[key]?.trim() !== ""
    );
    
    const isCmiDetails = isCmiProduct && hasValidProperties;

    const cmiAccordion = isCmiDetails && (
        <ManagedAccordionSection
            title={translate("Customer Material Details")}
            data-test-selector="productDetails_cmi_details"
            {...styles.specificationsAccordionSection}
            initialExpanded={true}
        >
            {properties["packagingCategory"] && properties["spoolSize"] && (
                <>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Size")}:</Typography>{" "}
                        {properties["spoolSize"]}
                    </Typography>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Material")}:</Typography>{" "}
                        {properties["packagingCategory"]}
                    </Typography>
                </>
            )}
            {!properties["packagingCategory"] && properties["spoolSize"] && (
                <>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Size")}:</Typography>{" "}
                        {properties["spoolSize"]}
                    </Typography>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Material")}:</Typography>{" "}
                        {translate("NO PREFERENCE")}
                    </Typography>
                </>
            )}
            {properties["packagingCategory"] && !properties["spoolSize"] && (
                <>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Size")}:</Typography>{" "}
                        {translate("NO PREFERENCE")}
                    </Typography>
                    <Typography {...styles.additionalVendorsText}>
                        <Typography {...styles.additionalVendorsLabel}>{translate("Spool Material")}:</Typography>{" "}
                        {properties["packagingCategory"]}
                    </Typography>
                </>
            )}

            {properties["serviceDesc"] && (
                <Typography {...styles.additionalVendorsText}>
                    <Typography {...styles.additionalVendorsLabel}>{translate("Custom Service")}:</Typography>{" "}
                    {properties["serviceDesc"]}
                </Typography>
            )}
        </ManagedAccordionSection>
    );

    /* Verndale Custom Feature - adding services accordion */
    const sevicesAccordion = properties.availableServices && (
        <ManagedAccordionSection
            title={translate("Services")}
            data-test-selector="productDetails_specifications_services"
            {...styles.specificationsAccordionSection}
        >
            <UnorderedList {...styles.servicesList}>
                {properties.availableServices.split("|").map((item, i) => (
                    <ListItem {...styles.cmiListItem} key={i}>
                        {item}
                    </ListItem>
                ))}
            </UnorderedList>
        </ManagedAccordionSection>
    );

    /* Verndale Custom Feature - adding additional vendors accordion */
    const getNonStandarVendors = (): JSX.Element => {
        const usVendorNames = properties["usVendorNames"];
        const usVendoMaterials = properties["usVendorMaterial"];

        const productNonStandardVendors: ProductNonStandardVendors[] = [];
        if (usVendorNames != null && usVendorNames !== "") {
            if (usVendoMaterials != null && usVendoMaterials !== "") {
                const usNames = usVendorNames.split(";");
                const usMaterials = usVendoMaterials.split(";");
                for (let idx = 0; idx < usNames.length; idx++) {
                    productNonStandardVendors.push({
                        name: usNames[idx],
                        material: usMaterials[idx],
                    });
                }
            } else {
                const usNames = usVendorNames.split(";");
                for (let idx = 0; idx < usNames.length; idx++) {
                    productNonStandardVendors.push({
                        name: usNames[idx],
                        material: "N/A",
                    });
                }
            }
        }

        return (
            <>
                {productNonStandardVendors.map((vendor, idx) => (
                    <Typography {...styles.additionalVendorsText} key={idx}>
                        <Typography {...styles.additionalVendorsLabel}>{vendor.name}:</Typography> {vendor.material}
                    </Typography>
                ))}
            </>
        );
    };
    const vendorsAccordion = properties["usVendorMaterial"] && properties["usVendorNames"] && (
        <ManagedAccordionSection
            title={translate("Additional Vendors")}
            data-test-selector="productDetails_specifications_vendors"
            {...styles.specificationsAccordionSection}
        >
            {getNonStandarVendors()}
        </ManagedAccordionSection>
    );

    /* Verndale Custom Feature - adding CMI products accordion */
    const cmiProductsAccordion = cmiProductsCount > 0 && (
        <ManagedAccordionSection
            title={translate("Customer Materials")}
            data-test-selector="productDetails_customerMaterials"
            uid="cmi-accordion"
            {...styles.specificationsAccordionSection}
        >
            <Typography {...styles.cmiSubheading}>{translate("Customer Materials Available")}</Typography>

            <UnorderedList {...styles.cmiList}>
                {products?.map(p => (
                    <ListItem {...styles.cmiListItem} key={p.id}>
                        <Link href={p.canonicalUrl}>{p.detail?.modelNumber}</Link>
                    </ListItem>
                ))}
            </UnorderedList>
        </ManagedAccordionSection>
    );

    /* Verndale Custom Feature - adding Return Policy accordion */
    const returnPolicyAccordion = returnPolicyContent && (
        <ManagedAccordionSection
            title="Return Policy"
            data-test-selector="productDetails_specifications_returnPolicy"
            {...styles.specificationsAccordionSection}
        >
            {parse(transformContentHtml(returnPolicyContent), parserOptions)}
        </ManagedAccordionSection>
    );

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                (!renderingSpecifications && !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={(attributesFirst || (!renderingSpecifications && !renderingDocuments)) && !isCmiDetails}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                        <Typography {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}:&nbsp;
                        </Typography>
                        <Typography {...styles.attributeTypeValuesText}>
                            {attributeType
                                .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </StyledWrapper>
                ))}
        </ManagedAccordionSection>
    );
    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {cmiAccordion}
            {attributesFirst && attributesAccordion}
            {documentsFirst && documentsAccordion}
            {renderingSpecifications &&
                specifications?.map((specification, index) => {
                    return (
                        <ManagedAccordionSection
                            initialExpanded={
                                index === 0 &&
                                (!documentsFirst || !renderingDocuments) &&
                                (!attributesFirst || !renderingAttributes) &&
                                !isCmiDetails
                            }
                            key={specification.id.toString()}
                            title={specification.nameDisplay}
                            data-test-selector="productDetails_specifications_specification"
                            {...styles.specificationsAccordionSection}
                        >
                            {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                        </ManagedAccordionSection>
                    );
                })}
            {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion}
            {/* Verndale Custom Feature Start - Additional order messages */}
            {sevicesAccordion}
            {vendorsAccordion}
            {cmiProductsAccordion}
            {returnPolicyAccordion}
            {/* Verndale Custom Feature End - Additional order messages */}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsSpecifications) as any) as any,
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
            {
                name: fields.returnPolicyContent,
                displayName: "Return Policy Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
